<script setup lang="ts">
import type { z } from 'zod'
import type { FormSubmit } from '~/types'
import { gsap } from 'gsap'

const { t, locale } = useI18n()
const toast = useToast()
const { contactUs } = usePostRequests()
const { contactUsSchema } = useValidation()

type Schema = z.output<typeof contactUsSchema>

const section = ref<HTMLElement | null>(null)
const form = ref(null)

// First Name + Last Name combine into formData.name for Zod validation
const firstName = ref('')
const lastName = ref('')

const formData = ref<Schema>({
  subject: '',
  name: '',
  email: '',
  message: '',
  mobile: undefined,
})

watch([firstName, lastName], ([fn, ln]) => {
  formData.value.name = `${fn} ${ln}`.trim()
}, { immediate: true })

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onSuccess = (response: any) => {
  firstName.value = ''
  lastName.value = ''
  formData.value = { subject: '', name: '', email: '', message: '', mobile: undefined }
  toast.add({
    title: response.message,
    color: 'green',
    icon: 'i-approved-success',
  })
}

const { submit, inProgress } = useSubmit<Schema>(
  () => {
    const { subject, ...rest } = formData.value
    return contactUs({
      ...rest,
      message: subject ? `[${subject}] ${rest.message}` : rest.message,
    })
  },
  { onSuccess },
  form as FormSubmit<Schema>,
)

const SUPPORT_EMAIL = 'support@bookforme.com'

const contactMethods = computed(() => [
  {
    icon: 'i-heroicons-envelope-20-solid',
    iconBg: '#CB2097',
    label: t('page.contact_us.method_email_label'),
    desc: t('page.contact_us.method_email_desc'),
    value: SUPPORT_EMAIL,
    href: `mailto:${SUPPORT_EMAIL}`,
  },
  {
    icon: 'i-heroicons-chat-bubble-oval-left-ellipsis-20-solid',
    iconBg: '#2E83FB',
    label: t('page.contact_us.method_whatsapp_label'),
    desc: t('page.contact_us.method_whatsapp_desc'),
    value: WHATSAPP_NUMBER,
    href: WHATSAPP_HREF,
  },
  {
    icon: 'i-heroicons-chat-bubble-left-right-20-solid',
    iconBg: '#FDC02D',
    label: t('page.contact_us.method_chat_label'),
    desc: t('page.contact_us.method_chat_desc'),
    comingSoon: true,
  },
])

// Shared label ui for all form groups
const labelUi = {
  label: { base: 'block text-sm font-bold text-gray-900 mb-1.5' },
  error: 'mt-1 text-xs text-red-500',
}

// Shared input ui
const inputUi = {
  base: 'w-full bg-white border border-gray-300 rounded-xl shadow-none focus:ring-0 focus:border-deep-indigo',
  rounded: 'rounded-xl',
  padding: {
    xl: 'px-4 py-4',
  },
}

onMounted(() => {
  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
  tl.fromTo('.cu-left',
    { opacity: 0, x: -50 },
    { opacity: 1, x: 0, duration: 0.85 },
  )
    .fromTo('.cu-right',
      { opacity: 0, x: 50 },
      { opacity: 1, x: 0, duration: 0.85 },
      '-=0.65',
    )
})
</script>

<template>
  <section
    ref="section"
    class="min-h-svh bg-white py-24 md:pt-28 lg:pt-44 lg:pb-28 flex items-center">
    <div class="container">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-14 xl:gap-24 items-start">

        <!-- ── Left column: info + contact methods ── -->
        <div class="cu-left flex flex-col items-center text-center lg:items-start lg:text-start">

          <!-- Badge -->
          <div class="bfm-badge bg-creamy-yellow text-gray-900 px-3 md:px-4 py-1.5 md:py-2 text-xs sm:text-sm md:text-base mb-6 lg:mb-8">
            <UIcon name="i-heroicons-phone-20-solid" class="text-heart-purple size-3.5 sm:size-4 md:size-5" />
            {{ $t('page.contact_us.badge') }}
          </div>

          <!-- Headline -->
          <h1 class="text-[clamp(1.75rem,4vw,3rem)] font-bold leading-[1.2] rtl:leading-[1.5] text-deep-indigo mb-5 md:mb-6">
            {{ $t('page.contact_us.title_1') }}<span class="text-heart-purple">{{ $t('page.contact_us.title_2') }}</span>
            <br>
            {{ $t('page.contact_us.title_3') }}<span class="text-sky-blue">{{ $t('page.contact_us.title_4') }}</span>
          </h1>

          <!-- Subtitle -->
          <p class="text-sm sm:text-base lg:text-lg font-medium text-overcast-night leading-relaxed max-w-xs sm:max-w-sm lg:max-w-md mb-10 md:mb-12">
            {{ $t('page.contact_us.subtitle') }}
          </p>

          <!-- Contact method cards -->
          <div class="flex flex-col gap-5 w-full max-w-sm lg:max-w-none">
            <component
              :is="method.comingSoon ? 'div' : 'a'"
              v-for="method in contactMethods"
              :key="method.label"
              v-bind="method.comingSoon ? {} : { href: method.href }"
              class="flex items-center gap-5 p-5 md:p-6 rounded-xl border-2 border-gray-900 shadow-[4px_4px_0_#111827] bg-white transition-all duration-200"
              :class="method.comingSoon
                ? 'opacity-60 cursor-not-allowed select-none'
                : 'hover:shadow-[6px_6px_0_#111827] hover:-translate-x-0.5 hover:-translate-y-0.5'">
              <span
                class="flex-shrink-0 size-11 md:size-13 rounded-lg border-2 border-gray-900 flex items-center justify-center"
                :style="{ backgroundColor: method.iconBg }">
                <UIcon :name="method.icon" class="text-white size-5 md:size-6" />
              </span>
              <div class="flex-1 flex flex-col items-start text-start gap-0.5">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm md:text-base font-bold text-gray-900">{{ method.label }}</span>
                  <span
                    v-if="method.comingSoon"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-cheese-yellow border border-gray-900 text-gray-900 leading-none">
                    {{ $t('page.contact_us.coming_soon') }}
                  </span>
                </div>
                <span class="text-xs md:text-sm text-gray-500">{{ method.desc }}</span>
                <span class="text-sm md:text-base font-bold text-deep-indigo" dir="ltr">{{ method.value }}</span>
              </div>
            </component>
          </div>
        </div>

        <!-- ── Right column: form card ── -->
        <div class="cu-right w-full">
          <div class="rounded-3xl border-2 border-gray-900 shadow-[10px_10px_0_#111827] bg-amber-50 p-8 md:p-11 lg:p-12">

            <!-- Form header -->
            <h2 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {{ $t('page.contact_us.form_title') }}
            </h2>
            <p class="text-sm md:text-base text-gray-500 mb-8 md:mb-10">
              {{ $t('page.contact_us.form_subtitle') }}
            </p>

            <!-- Form -->
            <UForm
              ref="form"
              :state="formData"
              :schema="contactUsSchema"
              :validate-on="['submit']"
              class="flex flex-col gap-6"
              @submit="submit">

              <!-- Row 1: First Name + Last Name -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <UFormGroup
                  name="name"
                  :label="$t('page.contact_us.form_first_name')"
                  :ui="labelUi">
                  <UInput
                    v-model="firstName"
                    type="text"
                    :placeholder="$t('page.contact_us.form_first_name')"
                    color="white"
                    size="xl"
                    :ui="inputUi"
                  />
                </UFormGroup>

                <UFormGroup
                  :label="$t('page.contact_us.form_last_name')"
                  :ui="labelUi">
                  <UInput
                    v-model="lastName"
                    type="text"
                    :placeholder="$t('page.contact_us.form_last_name')"
                    color="white"
                    size="xl"
                    :ui="inputUi"
                  />
                </UFormGroup>
              </div>

              <!-- Email Address -->
              <UFormGroup
                :label="$t('label.email_address')"
                name="email"
                :ui="labelUi">
                <UInput
                  v-model="formData.email"
                  type="email"
                  placeholder="name@example.com"
                  dir="ltr"
                  :class="{ 'placeholder-right': locale === 'ar' }"
                  color="white"
                  size="xl"
                  :ui="inputUi"
                />
              </UFormGroup>

              <!-- Subject -->
              <UFormGroup
                name="subject"
                :label="$t('page.contact_us.form_subject')"
                :ui="labelUi">
                <UInput
                  v-model="formData.subject"
                  type="text"
                  :placeholder="$t('page.contact_us.form_subject')"
                  color="white"
                  size="xl"
                  :ui="inputUi"
                />
              </UFormGroup>

              <!-- Details textarea -->
              <UFormGroup
                :label="$t('page.contact_us.form_details')"
                name="message"
                :ui="labelUi">
                <CommonTextarea
                  v-model="formData.message"
                  :rows="6"
                  :max-length="500"
                  :dir="locale === 'ar' ? 'rtl' : 'ltr'"
                  :placeholder="$t('page.contact_us.form_details')"
                />
              </UFormGroup>

              <!-- Privacy note -->
              <p class="text-xs text-gray-400 leading-relaxed -mt-1">
                {{ $t('page.contact_us.note') }}
              </p>

              <!-- Submit button -->
              <button
                type="submit"
                :disabled="inProgress"
                class="bfm-btn bfm-btn-lg w-full bg-cheese-yellow disabled:opacity-60 disabled:cursor-not-allowed">
                <UIcon v-if="inProgress" name="i-heroicons-arrow-path-20-solid" class="animate-spin size-5" />
                {{ $t('label.submit') }}
              </button>

            </UForm>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>
